import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

export default function MonthlyRachunki() {
  const { user, currentCompany } = useApp();
  const { t } = useI18n();
  const [contracts, setContracts] = useState([]);
  const [rachunki, setRachunki] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewRachunek, setPreviewRachunek] = useState(null);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      // Load contracts
      const savedContracts = localStorage.getItem('user_contracts');
      if (savedContracts) {
        setContracts(JSON.parse(savedContracts));
      } else {
        // Demo contracts
        setContracts([
          {
            id: 1,
            type: 'umowa_dzielo',
            contractor_name: 'Jan Kowalski',
            contractor_pesel: '90051512345',
            work_description: 'Tworzenie oprogramowania',
            monthly_amount: 5000,
            start_date: '2026-01-01',
            end_date: '2026-12-31',
            kup_rate: 50,
          },
        ]);
      }

      // Load existing rachunki
      const savedRachunki = localStorage.getItem('rachunki');
      if (savedRachunki) {
        setRachunki(JSON.parse(savedRachunki));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function generateRachunek(contract, month, year) {
    const rachunekNumber = `R/${contract.id}/${String(month + 1).padStart(2, '0')}/${year}`;
    
    // Check if already exists
    const exists = rachunki.find(r => r.number === rachunekNumber);
    if (exists) {
      alert('Rachunek za ten miesiąc już istnieje');
      return;
    }

    const grossAmount = contract.monthly_amount;
    const kupRate = contract.kup_rate || 50;
    const taxBase = grossAmount * (1 - kupRate / 100);
    const tax = Math.round(taxBase * 0.12); // 12% PIT
    const netAmount = grossAmount - tax;

    const newRachunek = {
      id: Date.now(),
      number: rachunekNumber,
      contract_id: contract.id,
      contractor_name: contract.contractor_name,
      contractor_pesel: contract.contractor_pesel,
      work_description: contract.work_description,
      period_month: month,
      period_year: year,
      period_label: `${MONTHS_PL[month]} ${year}`,
      gross_amount: grossAmount,
      kup_rate: kupRate,
      kup_amount: grossAmount * (kupRate / 100),
      tax_base: taxBase,
      tax_amount: tax,
      net_amount: netAmount,
      issue_date: new Date().toISOString().split('T')[0],
      payment_date: new Date().toISOString().split('T')[0],
      status: 'draft',
      signed: false,
      signed_at: null,
      company_name: currentCompany?.name || 'WORKIFYPL SP. Z O.O.',
      company_nip: currentCompany?.nip || '',
      created_at: new Date().toISOString(),
    };

    const updated = [newRachunek, ...rachunki];
    setRachunki(updated);
    localStorage.setItem('rachunki', JSON.stringify(updated));
    setPreviewRachunek(newRachunek);
  }

  function generateAllMissing(contract) {
    setGenerating(true);
    
    const startDate = new Date(contract.start_date);
    const endDate = contract.end_date ? new Date(contract.end_date) : new Date();
    const now = new Date();
    
    let generated = 0;
    let current = new Date(startDate);
    
    while (current <= now && current <= endDate) {
      const month = current.getMonth();
      const year = current.getFullYear();
      const rachunekNumber = `R/${contract.id}/${String(month + 1).padStart(2, '0')}/${year}`;
      
      const exists = rachunki.find(r => r.number === rachunekNumber);
      if (!exists) {
        generateRachunek(contract, month, year);
        generated++;
      }
      
      current.setMonth(current.getMonth() + 1);
    }
    
    setGenerating(false);
    alert(`✅ Wygenerowano ${generated} rachunków`);
  }

  function signRachunek(rachunek) {
    const updated = rachunki.map(r => 
      r.id === rachunek.id 
        ? { ...r, signed: true, signed_at: new Date().toISOString(), status: 'signed' }
        : r
    );
    setRachunki(updated);
    localStorage.setItem('rachunki', JSON.stringify(updated));
    
    if (previewRachunek?.id === rachunek.id) {
      setPreviewRachunek({ ...previewRachunek, signed: true, signed_at: new Date().toISOString(), status: 'signed' });
    }
  }

  function downloadPdf(rachunek) {
    const content = generateRachunekPdf(rachunek);
    
    // Create printable HTML
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  }

  function generateRachunekPdf(r) {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Rachunek ${r.number}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { text-align: center; font-size: 24px; margin-bottom: 30px; }
    .header { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .box { border: 1px solid #000; padding: 15px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { border: 1px solid #000; padding: 10px; text-align: left; }
    th { background: #f0f0f0; }
    .right { text-align: right; }
    .signature { margin-top: 50px; display: flex; justify-content: space-between; }
    .signature-box { width: 200px; text-align: center; border-top: 1px solid #000; padding-top: 10px; }
    .stamp { color: green; font-weight: bold; margin-top: 20px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <h1>RACHUNEK DO UMOWY O DZIEŁO</h1>
  <p style="text-align: center; margin-bottom: 30px;">Nr: <strong>${r.number}</strong></p>
  
  <div class="header">
    <div>
      <strong>ZAMAWIAJĄCY:</strong><br>
      ${r.company_name}<br>
      NIP: ${r.company_nip || '_______________'}
    </div>
    <div style="text-align: right;">
      <strong>Data wystawienia:</strong><br>
      ${r.issue_date}<br><br>
      <strong>Data zapłaty:</strong><br>
      ${r.payment_date}
    </div>
  </div>

  <div class="box">
    <strong>WYKONAWCA:</strong><br>
    ${r.contractor_name}<br>
    PESEL: ${r.contractor_pesel}<br>
  </div>

  <div class="box">
    <strong>Za wykonanie dzieła w okresie: ${r.period_label}</strong><br><br>
    <em>${r.work_description}</em>
  </div>

  <table>
    <tr>
      <th>Opis</th>
      <th class="right">Kwota</th>
    </tr>
    <tr>
      <td>Wynagrodzenie brutto</td>
      <td class="right">${r.gross_amount.toFixed(2)} PLN</td>
    </tr>
    <tr>
      <td>Koszty uzyskania przychodu (${r.kup_rate}%)</td>
      <td class="right">- ${r.kup_amount.toFixed(2)} PLN</td>
    </tr>
    <tr>
      <td>Podstawa opodatkowania</td>
      <td class="right">${r.tax_base.toFixed(2)} PLN</td>
    </tr>
    <tr>
      <td>Zaliczka na podatek dochodowy (12%)</td>
      <td class="right">- ${r.tax_amount.toFixed(2)} PLN</td>
    </tr>
    <tr style="font-weight: bold; background: #f0f0f0;">
      <td>DO WYPŁATY (netto)</td>
      <td class="right">${r.net_amount.toFixed(2)} PLN</td>
    </tr>
  </table>

  <p><strong>Słownie:</strong> ${numberToWords(r.net_amount)} złotych</p>

  ${r.signed ? `
  <div class="stamp">
    ✓ PODPISANO ELEKTRONICZNIE<br>
    Data: ${new Date(r.signed_at).toLocaleString('pl-PL')}<br>
    Wykonawca: ${r.contractor_name}
  </div>
  ` : ''}

  <div class="signature">
    <div class="signature-box">
      Podpis Zamawiającego
    </div>
    <div class="signature-box">
      Podpis Wykonawcy
    </div>
  </div>

  <p style="margin-top: 40px; font-size: 12px; color: #666;">
    Wygenerowano przez WORKIFYPL.com | ${new Date().toLocaleString('pl-PL')}
  </p>
</body>
</html>`;
  }

  if (loading) return <div className="page"><BackButton /><div className="loader">Ładowanie...</div></div>;

  // Preview mode
  if (previewRachunek) {
    return (
      <div className="page">
        <BackButton onClick={() => setPreviewRachunek(null)} />
        <h1>🧾 Rachunek {previewRachunek.number}</h1>

        <div style={{
          background: previewRachunek.signed ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 'bold' }}>{previewRachunek.contractor_name}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>PESEL: {previewRachunek.contractor_pesel}</div>
            </div>
            <div style={{
              background: previewRachunek.signed ? '#10b981' : '#f59e0b',
              padding: '4px 12px',
              borderRadius: 20,
              fontSize: 12,
            }}>
              {previewRachunek.signed ? '✓ Podpisany' : '⏳ Do podpisu'}
            </div>
          </div>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ opacity: 0.7, fontSize: 12 }}>Okres rozliczeniowy</div>
            <div style={{ fontWeight: 'bold', fontSize: 18 }}>{previewRachunek.period_label}</div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ opacity: 0.7, fontSize: 12 }}>Przedmiot</div>
            <div>{previewRachunek.work_description}</div>
          </div>

          <table style={{ width: '100%', fontSize: 14 }}>
            <tbody>
              <tr>
                <td style={{ padding: '8px 0', opacity: 0.8 }}>Brutto:</td>
                <td style={{ padding: '8px 0', textAlign: 'right' }}>{previewRachunek.gross_amount.toFixed(2)} PLN</td>
              </tr>
              <tr>
                <td style={{ padding: '8px 0', opacity: 0.8 }}>KUP ({previewRachunek.kup_rate}%):</td>
                <td style={{ padding: '8px 0', textAlign: 'right', color: '#10b981' }}>-{previewRachunek.kup_amount.toFixed(2)} PLN</td>
              </tr>
              <tr>
                <td style={{ padding: '8px 0', opacity: 0.8 }}>Podstawa:</td>
                <td style={{ padding: '8px 0', textAlign: 'right' }}>{previewRachunek.tax_base.toFixed(2)} PLN</td>
              </tr>
              <tr>
                <td style={{ padding: '8px 0', opacity: 0.8 }}>Podatek (12%):</td>
                <td style={{ padding: '8px 0', textAlign: 'right', color: '#ef4444' }}>-{previewRachunek.tax_amount.toFixed(2)} PLN</td>
              </tr>
              <tr style={{ borderTop: '2px solid rgba(255,255,255,0.2)' }}>
                <td style={{ padding: '12px 0', fontWeight: 'bold' }}>Do wypłaty:</td>
                <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 'bold', fontSize: 20, color: '#10b981' }}>
                  {previewRachunek.net_amount.toFixed(2)} PLN
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!previewRachunek.signed && (
            <button
              className="btn btn-primary"
              style={{ width: '100%' }}
              onClick={() => signRachunek(previewRachunek)}
            >
              ✍️ Podpisz elektronicznie
            </button>
          )}
          
          <button
            className="btn"
            style={{ width: '100%', background: 'rgba(99,102,241,0.3)' }}
            onClick={() => downloadPdf(previewRachunek)}
          >
            📥 Pobierz / Drukuj PDF
          </button>
        </div>

        {previewRachunek.signed && (
          <div style={{
            background: 'rgba(16,185,129,0.1)',
            borderRadius: 8,
            padding: 12,
            marginTop: 16,
            textAlign: 'center',
            fontSize: 13,
          }}>
            ✅ Podpisano elektronicznie<br />
            <span style={{ opacity: 0.7 }}>
              {new Date(previewRachunek.signed_at).toLocaleString('pl-PL')}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <BackButton />
      <h1>🧾 Rachunki miesięczne</h1>

      {/* Info */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📋 Rachunki do umów o dzieło</div>
        <p style={{ fontSize: 13, opacity: 0.9, margin: 0 }}>
          Automatyczna generacja miesięcznych rachunków z obliczeniem podatku i KUP 50%.
          Podpisz elektronicznie i pobierz PDF.
        </p>
      </div>

      {/* Contracts */}
      <h3>📑 Umowy ({contracts.length})</h3>
      {contracts.length === 0 ? (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 24,
          textAlign: 'center',
        }}>
          <p style={{ opacity: 0.7 }}>Brak umów o dzieło</p>
          <button className="btn btn-primary" onClick={() => {}}>
            + Dodaj umowę
          </button>
        </div>
      ) : (
        contracts.map(contract => {
          const contractRachunki = rachunki.filter(r => r.contract_id === contract.id);
          const unsignedCount = contractRachunki.filter(r => !r.signed).length;
          
          return (
            <div key={contract.id} style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{contract.contractor_name}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{contract.work_description}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 'bold', color: '#10b981' }}>{contract.monthly_amount} PLN/mies.</div>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>KUP: {contract.kup_rate}%</div>
                </div>
              </div>

              {unsignedCount > 0 && (
                <div style={{
                  background: 'rgba(245,158,11,0.2)',
                  borderRadius: 6,
                  padding: 8,
                  marginBottom: 12,
                  fontSize: 12,
                  textAlign: 'center',
                }}>
                  ⚠️ {unsignedCount} rachunków do podpisu
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-small"
                  style={{ background: 'rgba(16,185,129,0.3)' }}
                  onClick={() => generateRachunek(contract, currentMonth, currentYear)}
                >
                  + Rachunek za {MONTHS_PL[currentMonth]}
                </button>
                <button
                  className="btn btn-small"
                  style={{ background: 'rgba(99,102,241,0.3)' }}
                  onClick={() => generateAllMissing(contract)}
                  disabled={generating}
                >
                  {generating ? '⏳...' : '⚡ Generuj wszystkie brakujące'}
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* Rachunki List */}
      <h3 style={{ marginTop: 24 }}>🧾 Rachunki ({rachunki.length})</h3>
      
      {rachunki.length === 0 ? (
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 12,
          padding: 24,
          textAlign: 'center',
        }}>
          <p style={{ opacity: 0.7 }}>Brak rachunków</p>
        </div>
      ) : (
        <div className="list">
          {rachunki.map(r => (
            <div
              key={r.id}
              onClick={() => setPreviewRachunek(r)}
              style={{
                background: r.signed ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: 12,
                marginBottom: 8,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontWeight: 'bold', fontSize: 14 }}>{r.number}</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{r.contractor_name} • {r.period_label}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 'bold', color: '#10b981' }}>{r.net_amount.toFixed(2)} PLN</div>
                <div style={{
                  fontSize: 10,
                  color: r.signed ? '#10b981' : '#f59e0b',
                }}>
                  {r.signed ? '✓ Podpisany' : '⏳ Do podpisu'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper: Number to Polish words
function numberToWords(num) {
  const units = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć'];
  const teens = ['dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście', 'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
  const tens = ['', '', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt', 'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
  const hundreds = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset', 'osiemset', 'dziewięćset'];
  
  const n = Math.floor(num);
  if (n === 0) return 'zero';
  if (n >= 10000) return n.toString();
  
  let result = '';
  
  const th = Math.floor(n / 1000);
  const h = Math.floor((n % 1000) / 100);
  const t = Math.floor((n % 100) / 10);
  const u = n % 10;
  
  if (th > 0) {
    if (th === 1) result += 'tysiąc ';
    else if (th < 5) result += units[th] + ' tysiące ';
    else result += units[th] + ' tysięcy ';
  }
  
  if (h > 0) result += hundreds[h] + ' ';
  
  if (t === 1) {
    result += teens[u] + ' ';
  } else {
    if (t > 0) result += tens[t] + ' ';
    if (u > 0) result += units[u] + ' ';
  }
  
  return result.trim();
}
